<template>
  <v-card>
    <v-card-title class="font-weight-medium secondary--text primary darken-1">
      <v-icon small color="secondary" class="mr-2">mdi-cash-refund</v-icon>
      Refund Payment
      <v-spacer />
      <v-btn small icon @click="$emit('close')">
        <v-icon small color="secondary">mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />

    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" />
      <div class="caption grey--text mt-2">Fetching transaction details</div>
    </div>

    <v-card-text v-else class="pt-4">
      <v-sheet outlined rounded class="pa-3 mb-4">
        <div class="caption grey--text text-uppercase mb-2">Payment</div>
        <v-row dense>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Student</div>
            <div class="body-2 font-weight-medium">
              {{ invoice.name || "—" }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">USN / Application ID</div>
            <div class="body-2">
              {{ invoice.usn_id || invoice.application_id || "—" }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Source</div>
            <div class="body-2">{{ invoice.source_label || "—" }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Fee Type</div>
            <div class="body-2">{{ invoice.fee_type || "—" }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Payment Term</div>
            <div class="body-2">{{ invoice.term_no || "—" }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Amount Paid</div>
            <div class="body-2 font-weight-bold primary--text">
              ₹{{ invoice.amount }}
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Receipt No</div>
            <div class="body-2">{{ invoice.receipt_number || "—" }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Paid On</div>
            <div class="body-2">{{ invoice.transaction_date || "—" }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="caption grey--text">Settled</div>
            <div class="body-2">{{ invoice.is_settled ? "Yes" : "No" }}</div>
          </v-col>
        </v-row>
      </v-sheet>

      <v-sheet outlined rounded class="pa-3 mb-4">
        <div class="caption grey--text text-uppercase mb-2">
          Transaction Details
        </div>

        <v-alert
          v-if="!transaction"
          dense
          text
          type="info"
          class="mb-0 caption"
        >
          No gateway transaction found for this payment. Invoice transaction ID:
          {{ invoice.transaction_id || "—" }}
        </v-alert>

        <v-row v-else dense>
          <v-col
            v-for="field in transactionFields"
            :key="field.label"
            cols="6"
            md="3"
          >
            <div class="caption grey--text">{{ field.label }}</div>
            <div class="body-2" :class="field.class">
              {{ field.value || "—" }}
            </div>
          </v-col>
        </v-row>
      </v-sheet>

      <v-alert
        v-if="existingRefund"
        dense
        text
        type="warning"
        class="mb-0 caption"
      >
        This payment was already refunded on
        {{ existingRefund.refunded_date }} (Txn
        {{ existingRefund.refund_transaction_id }}).
      </v-alert>

      <template v-else>
        <div class="caption grey--text text-uppercase mb-2">Refund Details</div>
        <v-form ref="form" lazy-validation>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                :value="`₹${invoice.amount}`"
                label="Refund Amount (full)"
                outlined
                dense
                hide-details="auto"
                readonly
                disabled
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.trim="refund.refund_transaction_id"
                label="Refund Transaction ID *"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedRefundDate"
                    label="Refund Date *"
                    outlined
                    dense
                    hide-details="auto"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-calendar"
                  />
                </template>
                <v-date-picker
                  v-model="refund.refunded_date"
                  :max="today"
                  @input="datePicker = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.trim="refund.bank_name"
                label="Bank Name"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.trim="refund.account_number"
                label="Account Number"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model.trim="refund.ifsc_code"
                label="IFSC Code"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="refund.payment_mode"
                :items="paymentModes"
                label="Payment Mode"
                outlined
                dense
                clearable
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12" md="8">
              <v-text-field
                v-model.trim="refund.remarks"
                label="Remarks"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
    </v-card-text>

    <v-divider />

    <v-card-actions class="px-4 pb-4 pt-3">
      <v-spacer />
      <v-btn text @click="$emit('close')">Cancel</v-btn>
      <v-btn
        class="primary darken-1 white--text"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        <v-icon left small>mdi-cash-refund</v-icon>
        Confirm Refund
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    payment: { type: Object, required: true },
  },

  data() {
    return {
      loading: false,
      saving: false,
      datePicker: false,
      invoice: {},
      transaction: null,
      existingRefund: null,
      paymentModes: ["NEFT", "RTGS", "IMPS", "UPI", "Cheque", "Bank Transfer"],
      refund: {
        refund_transaction_id: "",
        refunded_date: null,
        bank_name: "",
        account_number: "",
        ifsc_code: "",
        payment_mode: null,
        remarks: "",
      },
      rules,
    };
  },

  computed: {
    today() {
      return new Date().toISOString().substr(0, 10);
    },

    formattedRefundDate() {
      if (!this.refund.refunded_date) return "";
      const [y, m, d] = this.refund.refunded_date.split("-");
      return `${d}-${m}-${y}`;
    },

    transactionFields() {
      const t = this.transaction || {};
      return [
        { label: "Transaction ID", value: t.transaction_id },
        { label: "Order ID", value: t.order_id },
        { label: "Amount", value: t.amount ? `₹${t.amount}` : null },
        {
          label: "Status",
          value: t.auth_status,
          class: t.auth_status === "0300" ? "green--text text--darken-2" : "",
        },
        { label: "Mode", value: t.payment_method_type },
        { label: "Bank Ref No", value: t.bank_ref_no },
        { label: "Bank ID", value: t.bankid },
        {
          label: "Transaction Date",
          value: this.formatDateTime(t.transaction_date),
        },
        { label: "Particular", value: t.payment_detail },
        { label: "Surcharge", value: t.surcharge },
        { label: "Charged Amount", value: t.charge_amount },
        { label: "Error", value: t.transaction_error_desc },
      ];
    },

    canSave() {
      if (this.existingRefund || this.loading) return false;
      const r = this.refund;
      return !!(r.refund_transaction_id && r.refunded_date);
    },
  },

  created() {
    this.loadTransactionDetails();
    this.loadPaymentModes();
  },

  methods: {
    async loadPaymentModes() {
      try {
        const res = await this.$axios.get("/refund_payment_modes");
        if (res.data.data?.length) this.paymentModes = res.data.data;
      } catch (e) {
        // Keep the built-in list if the lookup is unavailable.
        console.log(e);
      }
    },

    async loadTransactionDetails() {
      this.loading = true;
      // Fall back to the row we already have so the dialog is never blank.
      this.invoice = { ...this.payment };
      try {
        const { source_type, source_id } = this.payment;
        const res = await this.$axios.get(
          `/refundable_payments/${source_type}/${source_id}/transaction_details`,
        );
        const data = res.data.data;
        this.invoice = data.payment || this.invoice;
        this.transaction = data.transaction;
        this.existingRefund = this.invoice.refund;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    formatDateTime(value) {
      if (!value) return null;
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        // Optional fields left blank go as null rather than empty strings.
        const refund = Object.fromEntries(
          Object.entries(this.refund).map(([key, value]) => [
            key,
            value === "" ? null : value,
          ]),
        );

        await this.$axios.post("/payment_refunds", {
          refund: {
            source_type: this.payment.source_type,
            source_id: this.payment.source_id,
            ...refund,
          },
        });
        this.$store.commit("showSnackbar", {
          message: "Refund recorded",
          color: "success",
        });
        this.$emit("saved");
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to record refund";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
